import React from 'react';
import useStyles from './styles';
import CreditChip from 'atoms/CreditChip';
import Fields from './Fields';
import * as IconMDI from 'mdi-material-ui';
import { useAuth } from '@elevator/ui';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { CreditFormValues, CreditInput } from 'types/credit';
import { Formik, FormikHelpers } from 'formik';
import { object as yupObject, string, boolean as yupBoolean } from 'yup';
import SectionHeader from '../../molecules/SectionHeader';
import { baseInitialValues } from './constants';

export { baseInitialValues } from './constants';

/**
 * CreatorsForm Component
 *
 * Displays a list of credits (artists, producers, directors, etc.) in an accordion format.
 * Each credit can be expanded to edit details or removed. Supports adding new credits
 * through an expandable form. Manages credit creation, updates, and deletion with
 * Apollo Client mutations.
 *
 * Features:
 * - Expandable/collapsible credit items
 * - Inline editing of credit details
 * - Add new credits functionality
 * - Remove credits with confirmation
 * - Form validation using Yup schema
 * - Integration with Apollo Client for mutations
 *
 * Used in:
 * - ContentStep (submission flow)
 * - Submission layout (edit flow, via SubmissionForm)
 *
 * @param props - Component props
 * @param props.credits - Array of credit inputs to display
 * @param props.removeCredit - Function called when a credit is removed
 * @param props.saveCredit - Function called when a credit is saved (created or updated)
 * @param props.label - Label text for the credits section (default: "Creators")
 * @param props.description - Description text displayed below the label
 */
const CreatorsForm = ({
  credits,
  removeCredit,
  saveCredit,
  header = true,
  label = 'Credits',
  description,
  canAddCredits = true,
}: CreatorsFormProps) => {
  const { classes } = useStyles();
  const [editIndex, setEditIndex] = React.useState(-1);
  const [initialValues, setInitialValues] = React.useState(baseInitialValues);
  const [isAddingCredit, setIsAddingCredit] = React.useState(false);
  const { currentUser } = useAuth();

  const validationSchema = yupObject<CreditFormValues>({
    type: string().required('A credit type is required'),
    name: string().required('This field is required'),
    email: string().email('Please enter a valid email address'),
    isSubmitter: yupBoolean().required('This field is required'),
    autoLinked: yupBoolean().required('This field is required'),
  });

  const handleAddNew = () => {
    setEditIndex(-1);
    setIsAddingCredit(true);
  };

  const handleClose = () => {
    setEditIndex(-1);
    setIsAddingCredit(false);
  };

  const editCredit = (index: number) => {
    // Set initial values for Formik component by retrieving the
    // proper values from the credits[] array.
    const initialValuesForEdit = {
      name: credits[index].name,
      email: credits[index].email,
      type: credits[index].type,
      isSubmitter: credits[index].isSubmitter,
      autoLinked: credits[index].autoLinked,
    };

    setEditIndex(index);
    setInitialValues(initialValuesForEdit);
  };

  const handleSubmitCredit = async (
    values: CreditFormValues,
    actions: FormikHelpers<CreditFormValues>,
  ) => {
    const { type, name, email, isSubmitter, autoLinked } = values;

    //restructure state to credit with socials array
    // Each social will be dynamically included in array based on empty strings
    const creditToSave: CreditInput = {
      type,
      name,
      email,
      isSubmitter,
      autoLinked,
    };

    await saveCredit(creditToSave, editIndex);

    actions.resetForm();

    if (isAddingCredit) {
      setIsAddingCredit(false);
    }
  };

  const renderCreditFields = (editMode: boolean) => {
    return (
      <Fields
        currentUser={currentUser}
        onCancel={() => {
          setIsAddingCredit(false);
          setEditIndex(-1);
        }}
        saveCredit={async (credit: CreditInput) => {
          return await saveCredit(credit, editIndex);
        }}
        editMode={editMode}
      />
    );
  };

  const isEditingExisting = editIndex !== -1;
  const showForm = canAddCredits && (isEditingExisting || isAddingCredit);

  return (
    <Formik
      validationSchema={validationSchema}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmitCredit}
    >
      <>
        <div className={classes.root} data-component="CreatorsForm">
          {header && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1}
            >
              <SectionHeader
                title={label}
                titleVariant="h2"
                description={description}
                divider={false}
              />
              <Button
                variant="outlined"
                onClick={isAddingCredit ? handleClose : handleAddNew}
                disabled={!canAddCredits}
              >
                <IconMDI.Plus />
              </Button>
            </Stack>
          )}
          {showForm && (
            <>
              {isEditingExisting && renderCreditFields(true)}
              {!isEditingExisting && isAddingCredit && renderCreditFields(false)}
            </>
          )}
          <div className={classes.creditGrid}>
            {credits.map((item, index) => {
              const { name, type, avatar } = item;
              const isActive = editIndex === index;

              return (
                <React.Fragment key={item.creditId || index}>
                  <CreditChip
                    variant={'edit'}
                    name={name}
                    avatar={avatar}
                    type={type}
                    isActive={isActive}
                    index={index}
                    editCredit={editCredit}
                    removeCredit={() => {
                      void removeCredit(item.creditId, index);
                    }}
                    setEditIndex={setEditIndex}
                  />
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </>
    </Formik>
  );
};

export interface CreatorsFormProps {
  credits: Array<CreditInput>;
  removeCredit: (creditId: string | undefined, index: number) => void | Promise<void>;
  saveCredit: (credit: CreditInput, index: number) => Promise<void>;
  header?: boolean;
  label?: string;
  description?: string;
  canAddCredits?: boolean;
}

export default CreatorsForm;
